import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "../../../Context/Auth.Context";
import { AdminContext } from "../../../Context/Admin.Context";
import { RoutingContext } from "../../../Context/Routing.Context";

import { useForm } from "../../../Hooks/FormHook/form-hook";
import { useHttpClient } from "../../../Hooks/HttpHook/http-hook";
import { useFeatureDefinitionsHook } from "../../../Hooks/use-context-hooks";

import {
  VALIDATOR_MINLENGTH,
  VALIDATOR_MAXLENGTH,
} from "../../../Utils/validators";

import PortalForm from "../../PortalComponents/FormElements/PortalForm";
import PortalFormRow from "../../PortalComponents/FormElements/PortalFormRow";
import PortalSectionTextBox from "../../PortalComponents/PortalUi/PortalSectionTextBox/PortalSectionTextBox";

import Input from "../../../Shared/Components/UI/FormElements/Input/Input";
import SelectInput from "../../../Shared/Components/UI/FormElements/Select/Select";
import Modal from "../../../Shared/Components/UI/TheModals/TheModal";
import TheButton from "../../../Shared/Components/UI/TheButton/TheButton";

const PRODUCT_TYPE_OPTIONS = [
  { en: "Front Load Washers", fr: "Laveuses à chargement frontal" },
  { en: "Front Load Dryers", fr: "Sécheuses à chargement frontal" },
  { en: "Top Load Washers", fr: "Laveuses à chargement vertical" },
  { en: "Styler", fr: "Styler" },
  { en: "Range", fr: "Cuisinières" },
  { en: "Refrigeration", fr: "Réfrigération" },
  { en: "Dishwasher", fr: "Lave-vaisselle" },
  { en: "Microwave", fr: "Fours à micro-ondes" },
];

const SELECT_PRODUCT_TYPE_OPTIONS = [
  { value: "", label: "Select Product Type", disabled: true },
  ...PRODUCT_TYPE_OPTIONS.map((type) => ({
    value: type.en,
    label: `${type.en} / ${type.fr}`,
  })),
];

const defaultModal = {
  show: false,
  title: "",
  message: "",
  errorList: [],
  actionText: "Close",
  wasSuccessful: false,
  onConfirm: null,
  onCancel: null,
  onClose: null,
  cancelText: "",
  confirmText: "",
};

const UpdateFeatureDefinitionsPage = () => {
  const redirect = useNavigate();

  const { token } = useContext(AuthContext);
  const { isPortalActionState, setIsActiveSection } = useContext(AdminContext);
  const { setIsRoutingState } = useContext(RoutingContext);

  const { sendRequest } = useHttpClient();
  const { fetchDefinitions } = useFeatureDefinitionsHook();

  const definitionId = isPortalActionState?.editingFeatureDefinition;

  const [productType, setProductType] = useState(null);
  const [loadedDefinition, setLoadedDefinition] = useState(null);
  const [modalInfo, setModalInfo] = useState(defaultModal);

  const [formState, inputHandler, setFormData] = useForm(
    {
      featureEn: { value: "", isValid: false },
      featureFr: { value: "", isValid: false },
      definitionEn: { value: "", isValid: false },
      definitionFr: { value: "", isValid: false },
    },
    false
  );

  const closeModal = () => {
    setModalInfo(defaultModal);
  };

  const handleProductTypeChange = (event) => {
    const selectedProductType = PRODUCT_TYPE_OPTIONS.find(
      (type) => type.en === event.target.value
    );

    setProductType(selectedProductType || null);
  };

  const handleSuccess = () => {
    closeModal();
    fetchDefinitions();
    setIsActiveSection(12);
    redirect("/admin");
  };

  useEffect(() => {
    const fetchDefinition = async () => {
      if (!definitionId) return;

      setIsRoutingState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));

      try {
        const response = await sendRequest(
          // `http://localhost:3000/feature-definition/${definitionId}`,
          `${process.env.REACT_APP_BACKEND_URL}/feature-definition/${definitionId}`,
          "GET",
          null,
          {
            Authorization: `Bearer ${token}`,
          }
        );

        const featureDefinition = response.featureDefinition;

        setLoadedDefinition(featureDefinition);
        setProductType(featureDefinition.productType);

        setFormData(
          {
            featureEn: {
              value: featureDefinition.feature?.en || "",
              isValid: true,
            },
            featureFr: {
              value: featureDefinition.feature?.fr || "",
              isValid: true,
            },
            definitionEn: {
              value: featureDefinition.definition?.en || "",
              isValid: true,
            },
            definitionFr: {
              value: featureDefinition.definition?.fr || "",
              isValid: true,
            },
          },
          true
        );

        setIsRoutingState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
      } catch (err) {
        setIsRoutingState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));

        setModalInfo({
          show: true,
          title: "Something went wrong",
          message: "The feature definition could not be loaded.",
          errorList: [],
          confirmText: "Go back",
          onConfirm: handleSuccess,
          onClose: handleSuccess,
        });
      }
    };

    fetchDefinition();
  }, [definitionId, sendRequest, token, setFormData, setIsRoutingState]);

  const validateForm = () => {
    const errors = [];

    if (!productType?.en || !productType?.fr) {
      errors.push("Product type is required.");
    }

    if (!formState.inputs.featureEn.value) {
      errors.push("English feature name is required.");
    }

    if (!formState.inputs.featureFr.value) {
      errors.push("French feature name is required.");
    }

    if (!formState.inputs.definitionEn.value) {
      errors.push("English definition is required.");
    }

    if (!formState.inputs.definitionFr.value) {
      errors.push("French definition is required.");
    }

    return errors;
  };

  const hasChanges = () => {
    if (!loadedDefinition) return false;

    return (
      productType?.en !== loadedDefinition.productType?.en ||
      productType?.fr !== loadedDefinition.productType?.fr ||
      formState.inputs.featureEn.value !== loadedDefinition.feature?.en ||
      formState.inputs.featureFr.value !== loadedDefinition.feature?.fr ||
      formState.inputs.definitionEn.value !== loadedDefinition.definition?.en ||
      formState.inputs.definitionFr.value !== loadedDefinition.definition?.fr
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm();

    if (errors.length > 0) {
      setModalInfo({
        show: true,
        title: "Feature definition update failed",
        message: "Please fix the errors below.",
        errorList: errors,
        confirmText: "Try again",
        onConfirm: closeModal,
        onClose: closeModal,
      });

      return;
    }

    if (!hasChanges()) {
      setModalInfo({
        show: true,
        title: "Nothing to update",
        message: "You must change something before submitting an update.",
        errorList: [],
        confirmText: "Go back",
        onConfirm: closeModal,
        onClose: closeModal,
      });

      return;
    }

    setIsRoutingState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    const payload = {
      productType,
      feature: {
        en: formState.inputs.featureEn.value,
        fr: formState.inputs.featureFr.value,
      },
      definition: {
        en: formState.inputs.definitionEn.value,
        fr: formState.inputs.definitionFr.value,
      },
    };

    try {
      const response = await sendRequest(
        // `http://localhost:3000/edit-feature-definition/${definitionId}`,
        `${process.env.REACT_APP_BACKEND_URL}/edit-feature-definition/${definitionId}`,
        "PATCH",
        JSON.stringify(payload),
        {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      );

      setIsRoutingState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));

      if (response.featureDefinition) {
        setModalInfo({
          show: true,
          title: "Feature definition updated",
          message: "The feature definition has been updated successfully.",
          errorList: [],
          confirmText: "Go to definitions",
          wasSuccessful: true,
          onConfirm: handleSuccess,
          onClose: handleSuccess,
        });
      }
    } catch (err) {
      setIsRoutingState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));

      setModalInfo({
        show: true,
        title: "Something went wrong",
        message: `The feature definition could not be updated. ${err.message}`,
        errorList: [],
        confirmText: "Try again",
        onConfirm: closeModal,
        onClose: closeModal,
      });
    }
  };

  if (!definitionId) {
    return <p>No feature definition selected.</p>;
  }

  if (!loadedDefinition) {
    return <p>Loading feature definition...</p>;
  }

  return (
    <>
      <Modal
        icon
        iconName="close"
        styleName="lightIconButton"
        iconOnClick={modalInfo.onClose}
        show={modalInfo.show}
        title={modalInfo.title}
        message={modalInfo.message}
        errorList={modalInfo.errorList}
        closeOnBackdropClick={false}
        actionText={modalInfo.actionText}
        actionBox={
          <>
            {modalInfo.onConfirm && (
              <span onClick={modalInfo.onConfirm}>
                <TheButton styleName="red" animated="true">
                  {modalInfo.confirmText}
                </TheButton>
              </span>
            )}
          </>
        }
      />

      <PortalForm
        title="Edit Feature Definition"
        subtitle="Admin Portal"
        headline="Update the selected public feature definition."
        onSubmit={handleSubmit}
      >
        <PortalSectionTextBox
          title="Product Type"
          headline="Select the product type this feature definition belongs to."
        />

        <PortalFormRow>
          <SelectInput
            id="productType"
            name="productType"
            value={productType?.en || ""}
            labelName="Product Type"
            onChange={handleProductTypeChange}
            options={SELECT_PRODUCT_TYPE_OPTIONS}
          />
        </PortalFormRow>

        <PortalSectionTextBox
          title="Feature Name"
          headline="Update the English and French feature names."
        />

        <PortalFormRow>
          <Input
            key={`featureEn-${loadedDefinition._id}`}
            id="featureEn"
            type="text"
            element="input"
            label="Feature Name (English)"
            validators={[VALIDATOR_MINLENGTH(2), VALIDATOR_MAXLENGTH(120)]}
            errorText="Feature name must be between 2 and 120 characters."
            onInput={inputHandler}
            initialValue={loadedDefinition.feature?.en || ""}
            initialIsValid={true}
          />

          <Input
            key={`featureFr-${loadedDefinition._id}`}
            id="featureFr"
            type="text"
            element="input"
            label="Feature Name (French)"
            validators={[VALIDATOR_MINLENGTH(2), VALIDATOR_MAXLENGTH(120)]}
            errorText="Feature name must be between 2 and 120 characters."
            onInput={inputHandler}
            initialValue={loadedDefinition.feature?.fr || ""}
            initialIsValid={true}
          />
        </PortalFormRow>

        <PortalSectionTextBox
          title="Feature Definition"
          headline="Update the English and French public-facing definitions."
        />

        <PortalFormRow>
          <Input
            key={`definitionEn-${loadedDefinition._id}`}
            id="definitionEn"
            type="text"
            element="textarea"
            rows={10}
            label="Definition (English)"
            validators={[VALIDATOR_MINLENGTH(8)]}
            errorText="Definition must be at least 8 characters."
            onInput={inputHandler}
            initialValue={loadedDefinition.definition?.en || ""}
            initialIsValid={true}
          />

          <Input
            key={`definitionFr-${loadedDefinition._id}`}
            id="definitionFr"
            type="text"
            element="textarea"
            rows={10}
            label="Definition (French)"
            validators={[VALIDATOR_MINLENGTH(8)]}
            errorText="Definition must be at least 8 characters."
            onInput={inputHandler}
            initialValue={loadedDefinition.definition?.fr || ""}
            initialIsValid={true}
          />
        </PortalFormRow>

        <PortalFormRow>
          <div>
            <TheButton type="submit" styleName="red">
              Update Feature Definition
            </TheButton>
          </div>
        </PortalFormRow>
      </PortalForm>
    </>
  );
};

export default UpdateFeatureDefinitionsPage;